/**
 * @documental
 * @export Subheading
 * @import
 * import { Subheading } from "capitalrx-components-2.0/lib/typography";
 * @use
 * The `Subheading` component should be used as the secondary heading for body content, after the [Heading](../Heading/README.md) component.
 * @example
 * import { FC } from "react";
 * import { Subheading, Paragraph } from "capitalrx-components-2.0/lib/typography";
 *
 * export const Component: FC = () => {
 *   return (
 *     <div>
 *       <Subheading>Custom Subheading</Subheading>
 *       <Paragraph>
 *         The quick brown fox jumps over the lazy dog.
 *       </Paragraph>
 *     </div>
 *   );
 * };
 * @output
 * <div>
 *   <h5 class="subheading"><span class="semibold">Custom Subheading</span></h5>
 *   <p class="paragraph">
 *     The quick brown fox jumps over the lazy dog.
 *   </p>
 * </div>
 */
import { FC, ReactNode } from "react";

import { BaseProps } from "../../BaseProps";
import { createComponentStyleHook } from "../../tokens";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { Semibold } from "../Semibold";
import { TextAlignment } from "../TextAlignment";

import tokenStyles from "./styles.module.css";

/**
 * The Subheading component properties.
 */
export interface SubheadingProps extends BaseProps {
  /**
   * The text alignment. If not specified defaults to `"left"`.
   */
  readonly textAlign?: TextAlignment;

  /** The component children. */
  readonly children?: ReactNode;

  /** The optional id of the subheading element. */
  readonly id?: string;
}

const useResolvedSubheadingStyles = createComponentStyleHook<SubheadingProps>({
  legacyStyles: () => import("./styles.module.scss"),
  tokenStyles,
  componentTokens: "subheading",
});

/**
 * Displays body subheading text. The subheading is automatically spaced apart from its siblings. However, if the subheading is the
 * first element it will not have any spacing above, and if it is the last element it will not have any spacing below.
 * Additionally, the subheading is unaffected by text sizing applied to the [Body](../Body/README.md) component.
 */
export const Subheading: FC<SubheadingProps> = (props) => {
  const { children, textAlign = "left", id } = props;
  const { mode, styles, StyleModeBoundary } = useResolvedSubheadingStyles(props);
  const baseHtmlProps = getHtmlPropsFromBaseProps(props, { className: [styles.Subheading, "subheading", `align-${textAlign}`] });

  return (
    <StyleModeBoundary mode={mode}>
      <h5 {...baseHtmlProps} id={id}>
        <Semibold>{children}</Semibold>
      </h5>
    </StyleModeBoundary>
  );
};
